import { CmsActionClient } from "./CmsActionClient";
import { CmsElementControllerFactory } from "./CmsElementControllerFactory";
import { CmsLiveSortableAdapter } from "./CmsLiveSortableAdapter";
import { CmsCanvas } from "./components/CmsCanvas";
import { CmsFieldEditor } from "./components/CmsFieldEditor";
import { CmsMediaFieldEditor } from "./components/CmsMediaFieldEditor";
import { CmsSelectionOverlay } from "./components/CmsSelectionOverlay";
import type { CmsEditableElementController, CmsElementTypeMeta, CmsSelection } from "./types";
import { Modal } from "../../utils/Modal";
import { initCmsTemplatePicker } from "../components/CmsTemplatePicker";

interface CanvasCallbacks {
    openInspector: (url: string, title: string) => void;
    reloadTree: () => void;
    getLocale?: () => string;
    getComponentPickerUrl?: () => string;
}

export class CmsBuilderCanvasController {
    readonly #controllers = new Map<HTMLElement, CmsEditableElementController>();
    readonly #factory: CmsElementControllerFactory;
    readonly #actionClient: CmsActionClient;
    readonly #canvasEvents: CmsCanvas;
    readonly #selectionOverlay: CmsSelectionOverlay;
    readonly #elementTypes: Record<string, string>;
    readonly #elementTypeMeta: Record<string, CmsElementTypeMeta>;
    readonly #sortables: CmsLiveSortableAdapter[] = [];
    readonly #templateSaveTimers = new Map<HTMLElement, number>();
    #selected: CmsEditableElementController | null = null;
    #activeTemplateField: HTMLElement | null = null;
    #mediaOverlayCleanup: (() => void) | null = null;
    #canvasTimer: number | null = null;
    #loader: HTMLElement | null = null;

    constructor(
        private readonly root: HTMLElement,
        private readonly host: HTMLElement,
        private readonly tplCanvas: string,
        private readonly callbacks: CanvasCallbacks,
        elementTypes: Record<string, string> = {},
        elementTypeMeta: Record<string, CmsElementTypeMeta> = {},
    ) {
        this.#elementTypes = this.filterElementTypes(elementTypes);
        this.#elementTypeMeta = elementTypeMeta;
        this.#factory = new CmsElementControllerFactory(this.#elementTypes, elementTypeMeta);
        this.#actionClient = new CmsActionClient(root);
        this.#canvasEvents = new CmsCanvas(root);
        this.#selectionOverlay = new CmsSelectionOverlay(root, host);
    }

    init(): void {
        this.mountEditables();
        this.#selectionOverlay.mount();
        this.host.addEventListener("click", (event) => this.onClick(event), true);
        this.host.addEventListener("dblclick", (event) => this.onDoubleClick(event));
        this.host.addEventListener("mouseover", (event) => this.onPointerHover(event));
        this.host.addEventListener("mouseout", (event) => this.onPointerLeave(event));
        this.host.addEventListener("dragenter", (event) => this.onTemplateMediaDrag(event, true));
        this.host.addEventListener("dragover", (event) => this.onTemplateMediaDrag(event, true));
        this.host.addEventListener("dragleave", (event) => this.onTemplateMediaLeave(event));
        this.host.addEventListener("drop", (event) => {
            void this.onTemplateMediaDrop(event);
        });
        this.root.addEventListener("cms:select-request", (event) => {
            const detail = (event as CustomEvent<{ type?: string; id?: string }>).detail;
            this.selectByIdentity(detail.type ?? "", detail.id ?? "");
        });
        this.mountSortables();
        this.#canvasEvents.notifyUpdated();
    }

    async load(pageId: number): Promise<void> {
        if (pageId <= 0 || !this.tplCanvas) return;
        await this.reload(pageId);
    }

    async reload(pageId: number, restore?: { type: string; id: string }): Promise<void> {
        if (pageId <= 0 || !this.tplCanvas) return;
        this.host.classList.add("is-loading-page");
        this.showPageLoader();
        const html = await this.fetchHtml(this.withLocale(this.tplCanvas.replace("__ID__", String(pageId))));
        if (html === null) {
            this.host.classList.remove("is-loading-page");
            this.hidePageLoader();
            return;
        }
        this.destroyControllers();
        this.host.innerHTML = html;
        this.#loader = null;
        this.host.classList.remove("is-loading-page");
        this.host.classList.add("is-page-changed");
        if (this.#canvasTimer !== null) window.clearTimeout(this.#canvasTimer);
        this.#canvasTimer = window.setTimeout(() => {
            this.host.classList.remove("is-page-changed");
            this.#canvasTimer = null;
        }, 420);
        this.mountEditables();
        this.mountSortables();
        this.#canvasEvents.notifyUpdated();
        if (restore) this.restoreSelection(restore.type, restore.id);
    }

    /**
     * 페이지 없이(파일 연결 등 비-페이지 메뉴 선택 시) 정적 안내 HTML을 캔버스에 표시한다.
     * 기존 요소 컨트롤러/셀렉션/sortable을 정리해 직전 페이지 섹션 잔상을 제거한다.
     */
    showMessage(html: string): void {
        this.destroyControllers();
        this.host.classList.remove("is-loading-page", "is-page-changed");
        this.host.innerHTML = html;
        this.#loader = null;
        this.#canvasEvents.notifyUpdated();
    }

    selectedIdentity(): { type: string; id: string } | null {
        const el = this.#selected?.getElement();
        if (!el) return null;
        return {
            type: el.dataset.cmsEditableType ?? "",
            id: el.dataset.cmsEditableId ?? "",
        };
    }

    selectByIdentity(type: string, id: string): boolean {
        if (!type || !id) return false;
        const el = this.host.querySelector<HTMLElement>(`[data-cms-editable-type="${type}"][data-cms-editable-id="${id}"]`);
        const controller = el ? this.#controllers.get(el) : null;
        if (!controller) return false;
        this.select(controller);
        el.scrollIntoView({ block: "nearest", behavior: "smooth" });
        return true;
    }

    clearSelection(): void {
        this.#selected?.unselect();
        this.#selected = null;
        this.#selectionOverlay.setSelected(null);
        this.emitSelection(null);
    }

    destroy(): void {
        this.destroyControllers();
        this.#selectionOverlay.destroy();
    }

    private mountEditables(): void {
        this.host.querySelectorAll<HTMLElement>("[data-cms-editable]").forEach((el) => {
            if (this.#controllers.has(el)) return;
            const controller = this.#factory.create(el);
            controller.mount();
            this.#controllers.set(el, controller);
        });
    }

    private destroyControllers(): void {
        this.clearSelection();
        this.clearTemplateField();
        this.destroySortables();
        this.#controllers.forEach((controller) => controller.destroy());
        this.#controllers.clear();
    }

    private mountSortables(): void {
        this.destroySortables();
        this.host.querySelectorAll<HTMLElement>("[data-cms-live-elements]").forEach((container) => {
            const sortable = new CmsLiveSortableAdapter(container, {
                onStart: () => {
                    this.clearSelection();
                    container.classList.add("is-dragging");
                },
                onEnd: (item, moved) => {
                    container.classList.remove("is-dragging");
                    if (!moved) return;
                    void this.saveDraggedOrder(container, item);
                },
            });
            sortable.mount();
            this.#sortables.push(sortable);
        });
    }

    private destroySortables(): void {
        while (this.#sortables.length > 0) {
            this.#sortables.pop()?.destroy();
        }
    }

    private async saveDraggedOrder(container: HTMLElement, item: HTMLElement): Promise<void> {
        container.classList.add("is-saving");
        const ok = await this.#actionClient.reorderContainer(container, "element");
        container.classList.remove("is-saving");
        if (!ok) {
            container.classList.add("is-error");
            await this.reloadCurrent(item);
            return;
        }
        await this.reloadCurrent(item, {
            type: "element",
            id: item.dataset.cmsEditableId ?? "",
        });
    }

    private onClick(event: MouseEvent): void {
        const target = event.target as HTMLElement | null;
        if (target?.closest("[data-cms-inline-editor-host], input, textarea, select, button, [data-code-editor], [data-rich-editor]")) {
            return;
        }
        const templateField = target?.closest<HTMLElement>("[data-cms-template-field]");
        if (templateField && this.host.contains(templateField)) {
            if (this.#activeTemplateField === templateField && templateField.getAttribute("contenteditable") === "true") {
                return;
            }
            event.preventDefault();
            event.stopPropagation();
            this.clearBrowserSelection();
            this.selectTemplateField(templateField);
            return;
        }
        const editable = target?.closest<HTMLElement>("[data-cms-editable]");
        if (!editable || !this.host.contains(editable)) return;
        event.preventDefault();
        event.stopPropagation();
        this.clearBrowserSelection();
        const controller = this.#controllers.get(editable);
        if (!controller) return;
        this.select(controller);
    }

    private onDoubleClick(event: MouseEvent): void {
        const target = event.target as HTMLElement | null;
        const templateField = target?.closest<HTMLElement>("[data-cms-template-field]");
        if (!templateField || !this.host.contains(templateField)) return;
        event.preventDefault();
        event.stopPropagation();
        this.activateTemplateField(templateField);
        if ((templateField.dataset.cmsTemplateFieldType ?? "text") === "media") {
            void this.replaceTemplateMedia(templateField);
        }
    }

    private onPointerHover(event: MouseEvent): void {
        const target = event.target as HTMLElement | null;
        const field = target?.closest<HTMLElement>("[data-cms-template-field]");
        if (field && this.host.contains(field)) {
            this.#selectionOverlay.setHovered(this.selectionFromTemplateField(field));
            return;
        }
        const editable = target?.closest<HTMLElement>("[data-cms-editable]");
        if (editable && this.host.contains(editable)) {
            this.#selectionOverlay.setHovered(CmsSelectionOverlay.selectionFromElement(editable));
        }
    }

    private onPointerLeave(event: MouseEvent): void {
        const related = event.relatedTarget as Node | null;
        if (related && this.host.contains(related)) return;
        this.#selectionOverlay.setHovered(null);
    }

    private onTemplateMediaDrag(event: DragEvent, active: boolean): void {
        const field = this.templateMediaFieldFromEvent(event);
        if (!field) return;
        event.preventDefault();
        event.stopPropagation();
        field.classList.toggle("is-cms-template-media-dragover", active);
    }

    private onTemplateMediaLeave(event: DragEvent): void {
        const field = this.templateMediaFieldFromEvent(event);
        if (!field) return;
        const related = event.relatedTarget as Node | null;
        if (related && field.contains(related)) return;
        field.classList.remove("is-cms-template-media-dragover");
    }

    private async onTemplateMediaDrop(event: DragEvent): Promise<void> {
        const field = this.templateMediaFieldFromEvent(event);
        if (!field) return;
        event.preventDefault();
        event.stopPropagation();
        field.classList.remove("is-cms-template-media-dragover");
        const file = event.dataTransfer?.files?.[0] ?? null;
        if (!file) return;
        this.activateTemplateField(field);
        await this.uploadTemplateMediaFile(field, file);
    }

    private templateMediaFieldFromEvent(event: Event): HTMLElement | null {
        const target = event.target as HTMLElement | null;
        const field = target?.closest<HTMLElement>("[data-cms-template-field-type=\"media\"]") ?? null;
        return field && this.host.contains(field) ? field : null;
    }

    private select(controller: CmsEditableElementController): void {
        this.clearTemplateField();
        this.#selected?.unselect();
        this.#selected = controller;
        controller.select();
        const element = controller.getElement();
        const selection = CmsSelectionOverlay.selectionFromElement(element);
        this.emitSelection(selection);
        this.#selectionOverlay.setSelected(selection);
        const editUrl = element.dataset.editUrl ?? "";
        if (editUrl !== "") {
            const isCollection = element.dataset.cmsCollection === "1" || element.dataset.cmsNodeType === "collection";
            const url = isCollection ? this.withQuery(editUrl, "context", "collection") : editUrl;
            const title = isCollection
                ? "Collection Inspector"
                : (element.dataset.cmsEditableType === "section" ? "Section Inspector" : `${element.dataset.typeLabel ?? "Element"} Inspector`);
            this.callbacks.openInspector(url, title);
        }
    }

    private selectTemplateField(field: HTMLElement): void {
        this.clearSelection();
        this.clearTemplateField();
        this.#activeTemplateField = field;
        field.classList.add("is-cms-template-field-active");
        const selection = this.selectionFromTemplateField(field);
        this.emitSelection(selection);
        this.#selectionOverlay.setSelected(selection);
        const editUrl = field.dataset.cmsTemplateSectionEditUrl ?? "";
        if (editUrl !== "") {
            const isCollectionSlot = selection.type === "collection-slot";
            let url = this.withQuery(editUrl, "context", isCollectionSlot ? "collection-slot" : "template-field");
            url = this.withQuery(url, "field_key", field.dataset.cmsTemplateField ?? "");
            url = this.withQuery(url, "field_type", field.dataset.cmsTemplateFieldType ?? "text");
            url = this.withQuery(url, "field_label", field.dataset.cmsTemplateFieldLabel ?? "Field");
            if (isCollectionSlot) {
                url = this.withQuery(url, "slot_key", field.dataset.cmsTemplateField ?? "");
            }
            const fieldType = field.dataset.cmsTemplateFieldType ?? "text";
            this.callbacks.openInspector(url, isCollectionSlot ? "Collection Slot Inspector" : (fieldType === "media" ? "Image Inspector" : "Text Inspector"));
        }
    }

    private selectionFromTemplateField(field: HTMLElement): CmsSelection {
        const collectionRoot = field.closest<HTMLElement>("[data-cms-node-type=\"collection\"], [data-cms-collection=\"1\"]");
        const isCollectionSlot = collectionRoot !== null;
        const slotKey = field.dataset.cmsTemplateField ?? "";
        return {
            type: isCollectionSlot ? "collection-slot" : "template-field",
            id: `${field.dataset.cmsTemplateSectionId ?? ""}:${field.dataset.cmsTemplateField ?? ""}`,
            label: field.dataset.cmsTemplateFieldLabel || "필드",
            sublabel: isCollectionSlot ? `slot:${slotKey}` : (field.dataset.cmsTemplateFieldType || "text"),
            element: field,
            fieldKey: slotKey,
            fieldType: field.dataset.cmsTemplateFieldType,
        };
    }

    private activateTemplateField(field: HTMLElement): void {
        this.selectTemplateField(field);

        const type = field.dataset.cmsTemplateFieldType ?? "text";
        if (type === "media") {
            return;
        }

        this.beginTemplateTextEdit(field);
    }

    private beginTemplateTextEdit(field: HTMLElement): void {
        field.dataset.cmsTemplateEditOriginalHtml = field.innerHTML;
        const keepCurrentSelection = CmsFieldEditor.hasSelection(field);
        if (keepCurrentSelection) {
            CmsFieldEditor.rememberSelection(field);
        }
        field.setAttribute("contenteditable", "true");
        field.setAttribute("spellcheck", "true");
        field.focus({ preventScroll: true });
        if (keepCurrentSelection) {
            CmsFieldEditor.restoreSelection(field);
        } else {
            this.placeCaretAtEnd(field);
        }
        CmsFieldEditor.rememberSelection(field);

        if (field.dataset.cmsTemplateTextBound === "1") {
            return;
        }
        field.dataset.cmsTemplateTextBound = "1";
        field.addEventListener("input", () => this.scheduleTemplateFieldSave(field));
        field.addEventListener("mouseup", () => CmsFieldEditor.rememberSelection(field));
        field.addEventListener("keyup", () => CmsFieldEditor.rememberSelection(field));
        field.addEventListener("touchend", () => CmsFieldEditor.rememberSelection(field));
        field.addEventListener("blur", () => {
            void this.flushTemplateFieldSave(field);
        });
        field.addEventListener("paste", (event) => {
            event.preventDefault();
            const text = event.clipboardData?.getData("text/plain") ?? "";
            document.execCommand("insertText", false, text);
        });
        field.addEventListener("keydown", (event) => {
            if ((event.metaKey || event.ctrlKey) && (event.key === "Enter" || event.key.toLowerCase() === "s")) {
                event.preventDefault();
                void this.flushTemplateFieldSave(field);
                return;
            }
            if (event.key === "Escape") {
                event.preventDefault();
                this.cancelTemplateTextEdit(field);
            }
        });
    }

    private placeCaretAtEnd(field: HTMLElement): void {
        const selection = window.getSelection();
        if (!selection) return;
        const range = document.createRange();
        range.selectNodeContents(field);
        range.collapse(false);
        selection.removeAllRanges();
        selection.addRange(range);
    }

    private clearBrowserSelection(): void {
        window.getSelection()?.removeAllRanges();
    }

    private clearTemplateField(): void {
        this.clearMediaOverlay();
        if (!this.#activeTemplateField) return;
        if (this.#activeTemplateField.getAttribute("contenteditable") === "true") {
            void this.flushTemplateFieldSave(this.#activeTemplateField);
        }
        this.#activeTemplateField.classList.remove("is-cms-template-field-active");
        this.#activeTemplateField.removeAttribute("contenteditable");
        this.#activeTemplateField.removeAttribute("spellcheck");
        this.#activeTemplateField = null;
        this.#selectionOverlay.setSelected(null);
        this.clearBrowserSelection();
    }

    private cancelTemplateTextEdit(field: HTMLElement): void {
        const original = field.dataset.cmsTemplateEditOriginalHtml;
        if (original !== undefined) {
            field.innerHTML = original;
        }
        const existing = this.#templateSaveTimers.get(field);
        if (existing != null) {
            window.clearTimeout(existing);
            this.#templateSaveTimers.delete(field);
        }
        field.removeAttribute("contenteditable");
        field.removeAttribute("spellcheck");
        field.classList.remove("is-cms-template-field-dirty");
        this.#selectionOverlay.schedule();
        this.clearBrowserSelection();
    }

    private clearMediaOverlay(): void {
        if (!this.#mediaOverlayCleanup) return;
        this.#mediaOverlayCleanup();
        this.#mediaOverlayCleanup = null;
    }

    private scheduleTemplateFieldSave(field: HTMLElement): void {
        const existing = this.#templateSaveTimers.get(field);
        if (existing != null) window.clearTimeout(existing);
        const timer = window.setTimeout(() => {
            this.#templateSaveTimers.delete(field);
            void this.saveTemplateTextField(field, true);
        }, 650);
        this.#templateSaveTimers.set(field, timer);
    }

    private async flushTemplateFieldSave(field: HTMLElement): Promise<void> {
        const existing = this.#templateSaveTimers.get(field);
        if (existing != null) {
            window.clearTimeout(existing);
            this.#templateSaveTimers.delete(field);
        }
        await this.saveTemplateTextField(field, true);
    }

    private async saveTemplateTextField(field: HTMLElement, silent = false): Promise<boolean> {
        const payload = CmsFieldEditor.payload(field);
        const value = String(payload.value ?? "");
        const ok = await this.saveTemplateField(field, payload, silent);
        if (ok) {
            field.dataset.cmsTemplateFieldValue = value;
            field.dataset.cmsTemplateFieldHtml = String(payload.html ?? "");
            field.classList.remove("is-cms-template-field-dirty");
            this.#selectionOverlay.schedule();
        } else {
            field.classList.add("is-cms-template-field-dirty");
        }
        return ok;
    }

    private async replaceTemplateMedia(field: HTMLElement): Promise<void> {
        const uploadUrl = field.dataset.cmsTemplateUploadUrl ?? "";
        if (uploadUrl === "") {
            await Modal.alert("업로드 경로가 설정되지 않았습니다.");
            return;
        }
        const file = await CmsMediaFieldEditor.pick();
        if (!file) return;
        await this.uploadTemplateMediaFile(field, file);
    }

    private async uploadTemplateMediaFile(field: HTMLElement, file: File): Promise<void> {
        const uploadUrl = field.dataset.cmsTemplateUploadUrl ?? "";
        if (uploadUrl === "") {
            await Modal.alert("업로드 경로가 설정되지 않았습니다.");
            return;
        }
        try {
            CmsMediaFieldEditor.setLoading(field, true, "업로드 중...");
            const item = await CmsMediaFieldEditor.upload(file, uploadUrl);
            if (!item) {
                await Modal.alert("이미지 또는 영상 파일만 업로드할 수 있습니다.");
                return;
            }

            CmsMediaFieldEditor.setLoading(field, true, "저장 중...");
            const ok = await this.saveTemplateField(field, {
                src: item.file_url,
                attachment_id: item.id,
                media_type: item.media_type ?? item.file_type,
                poster: item.poster_url ?? item.thumbnail_url ?? "",
            });
            if (ok) await this.reloadCurrent(field);
        } finally {
            CmsMediaFieldEditor.setLoading(field, false);
        }
    }

    private async saveTemplateField(
        field: HTMLElement,
        payload: Record<string, string | number | boolean | null | undefined>,
        silent = false,
    ): Promise<boolean> {
        const url = field.dataset.cmsTemplateUpdateUrl ?? "";
        const fieldKey = field.dataset.cmsTemplateField ?? "";
        if (url === "" || fieldKey === "") return false;
        const json = await this.#actionClient.sendForm(url, {
            field_key: fieldKey,
            locale: field.dataset.cmsTemplateLocale ?? this.callbacks.getLocale?.() ?? "",
            ...payload,
        }, "PATCH", { silent });
        return json !== null;
    }

    private withQuery(url: string, key: string, value: string): string {
        const glue = url.includes("?") ? "&" : "?";
        return `${url}${glue}${encodeURIComponent(key)}=${encodeURIComponent(value)}`;
    }

    private withLocale(url: string): string {
        const locale = this.callbacks.getLocale?.() ?? "";
        return locale !== "" ? this.withQuery(url, "locale", locale) : url;
    }

    private showPageLoader(): void {
        if (!this.#loader) {
            const loader = document.createElement("div");
            loader.className = "admin-cms-builder__canvas-loader";
            loader.setAttribute("role", "status");
            loader.setAttribute("aria-live", "polite");
            loader.innerHTML = '<span class="admin-cms-builder__canvas-loader-icon"><i class="fa-solid fa-spinner fa-spin" aria-hidden="true"></i></span><span>페이지 구조 불러오는 중</span>';
            this.host.appendChild(loader);
            this.#loader = loader;
        }
        this.#loader.hidden = false;
    }

    private hidePageLoader(): void {
        if (this.#loader) this.#loader.hidden = true;
    }

    private async reloadCurrent(target: HTMLElement, restore?: { type: string; id: string }): Promise<void> {
        const canvas = target.closest<HTMLElement>("[data-cms-canvas]");
        const pageId = parseInt(canvas?.dataset.pageId ?? this.root.dataset.pageId ?? "0", 10);
        await this.reload(pageId, restore ?? this.selectedIdentity() ?? undefined);
    }

    private restoreSelection(type: string, id: string): void {
        if (!type || !id) return;
        const el = this.host.querySelector<HTMLElement>(`[data-cms-editable-type="${type}"][data-cms-editable-id="${id}"]`);
        const controller = el ? this.#controllers.get(el) : null;
        if (controller) this.select(controller);
    }

    private emitSelection(selection: CmsSelection | null): void {
        const nodeRect = this.rectDetail(selection?.element ?? null);
        const overlayRect = this.rectDetail(this.root.querySelector<HTMLElement>(".admin-cms-selection-overlay__box--selected:not([hidden])"));
        this.root.dispatchEvent(new CustomEvent("cms:selection-changed", {
            bubbles: true,
            detail: {
                ...(selection ?? { type: "", id: "", label: "" }),
                nodeType: selection?.type === "collection-slot"
                    ? "collection-slot"
                    : (selection?.element?.dataset.cmsNodeType ?? selection?.fieldType ?? selection?.type ?? ""),
                nodeRect,
                overlayRect,
            },
        }));
    }

    private rectDetail(element: HTMLElement | null): Record<string, number> | null {
        if (!element || element.hidden || !document.documentElement.contains(element)) return null;
        const rect = element.getBoundingClientRect();
        return {
            x: Math.round(rect.x),
            y: Math.round(rect.y),
            width: Math.round(rect.width),
            height: Math.round(rect.height),
        };
    }

    private async fetchHtml(url: string): Promise<string | null> {
        try {
            const res = await fetch(url, { headers: { "X-Requested-With": "XMLHttpRequest" } });
            if (!res.ok) {
                console.error(`CMS builder fragment request failed: ${res.status} ${res.statusText}`, url);
                return null;
            }
            return await res.text();
        } catch (error) {
            console.error("CMS builder fragment request failed.", error);
            return null;
        }
    }

    private async pickTemplateComponent(): Promise<string | null> {
        const url = this.callbacks.getComponentPickerUrl?.() ?? "";
        if (url === "") {
            await Modal.alert("템플릿 컴포넌트 선택기가 설정되지 않았습니다.");
            return null;
        }
        const html = await this.fetchHtml(url);
        if (html === null) {
            await Modal.alert("템플릿 컴포넌트 목록을 불러오지 못했습니다.");
            return null;
        }

        const pending = Modal.content(html, "템플릿 컴포넌트 선택", "wide");
        return await new Promise((resolve) => {
            window.setTimeout(() => {
                const root = document.getElementById("app-modal-root");
                const picker = root?.querySelector<HTMLElement>("[data-template-component-picker]");
                if (!picker) {
                    resolve(null);
                    return;
                }
                const close = root?.querySelector<HTMLButtonElement>("[data-app-modal-close]");
                root?.querySelector<HTMLButtonElement>("[data-app-modal-ok]")?.replaceChildren("닫기");
                initCmsTemplatePicker(picker, (slug) => {
                    close?.click();
                    resolve(slug !== "" ? slug : null);
                });
            }, 0);
            void pending.then(() => resolve(null));
        });
    }

    private async pickElementType(): Promise<string | null> {
        const entries = Object.entries(this.#elementTypes);
        if (entries.length === 0) {
            await Modal.alert("추가할 수 있는 요소 유형이 없습니다.");
            return null;
        }

        const cards = entries.map(([type, label]) => {
            const icon = this.#elementTypeMeta[type]?.icon ?? "fa-solid fa-cube";
            return `<button type="button" class="admin-cms-template-picker__card admin-cms-template-picker__card--element" data-cms-element-type-choice data-element-type="${this.escapeHtml(type)}" data-search="${this.escapeHtml(`${type} ${label}`.toLowerCase())}">` +
                `<span class="admin-cms-template-picker__thumb admin-cms-template-picker__thumb--icon"><i class="${this.escapeHtml(icon)}" aria-hidden="true"></i></span>` +
                `<span class="admin-cms-template-picker__meta"><strong>${this.escapeHtml(label)}</strong><code>${this.escapeHtml(type)}</code></span>` +
                `<span class="admin-cms-template-picker__action" aria-hidden="true"><i class="fa-solid fa-arrow-right"></i></span>` +
                `</button>`;
        }).join("");

        const html = `<div class="admin-cms-template-picker" data-cms-element-type-picker data-cms-element-type-count="${entries.length}">` +
            `<div class="admin-cms-template-picker__summary"><div><span class="admin-cms-template-picker__eyebrow">콘텐츠 요소</span><p>페이지에 넣을 요소를 선택하면 바로 추가됩니다.</p></div><span class="admin-badge admin-badge--default">${entries.length}개</span></div>` +
            `<div class="admin-cms-template-picker__search"><label class="admin-form__label" for="cms-element-type-search">검색</label><input type="text" id="cms-element-type-search" class="admin-form__input" placeholder="텍스트, 이미지, HTML..." data-cms-element-type-search></div>` +
            `<div class="admin-cms-template-picker__groups" data-lenis-prevent><section class="admin-cms-template-picker__group"><h3 class="admin-cms-template-picker__group-title">전체 요소</h3><div class="admin-cms-template-picker__grid">${cards}</div><p class="admin-cms-template-picker__empty" data-cms-element-type-empty hidden>검색 결과가 없습니다.</p></section></div>` +
            `</div>`;

        const pending = Modal.content(html, "요소 추가", "wide");
        return await new Promise((resolve) => {
            window.setTimeout(() => {
                const root = document.getElementById("app-modal-root");
                const picker = root?.querySelector<HTMLElement>("[data-cms-element-type-picker]");
                if (!picker) {
                    resolve(null);
                    return;
                }
                const close = root?.querySelector<HTMLButtonElement>("[data-app-modal-close]");
                const search = picker.querySelector<HTMLInputElement>("[data-cms-element-type-search]");
                search?.addEventListener("input", () => {
                    const q = search.value.trim().toLowerCase();
                    let visible = 0;
                    picker.querySelectorAll<HTMLElement>("[data-cms-element-type-choice]").forEach((card) => {
                        const matched = q === "" || (card.dataset.search ?? "").includes(q);
                        card.hidden = !matched;
                        if (matched) visible += 1;
                    });
                    const empty = picker.querySelector<HTMLElement>("[data-cms-element-type-empty]");
                    if (empty) empty.hidden = visible > 0;
                });
                picker.addEventListener("click", (event) => {
                    const choice = (event.target as HTMLElement | null)?.closest<HTMLElement>("[data-cms-element-type-choice]");
                    if (!choice) return;
                    event.preventDefault();
                    const type = choice.dataset.elementType ?? "";
                    close?.click();
                    resolve(type !== "" ? type : null);
                }, { once: false });
            }, 0);
            void pending.then(() => resolve(null));
        });
    }

    private filterElementTypes(types: Record<string, string>): Record<string, string> {
        const filtered: Record<string, string> = {};
        Object.entries(types).forEach(([key, label]) => {
            if (key === "template_field") return;
            filtered[key] = label;
        });
        return filtered;
    }

    private escapeHtml(value: string): string {
        return value
            .replace(/&/g, "&amp;")
            .replace(/</g, "&lt;")
            .replace(/>/g, "&gt;")
            .replace(/"/g, "&quot;");
    }
}
